/**
 * Mini-checklist "Setup completo?" mostrato in cima alle Settings.
 * Verifica i campi chiave per generare preventivi professionali e flagga
 * quelli ancora vuoti come "da completare". Aiuta il neo-utente a non
 * lasciare il setup a metà.
 */
import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { useOrgProfile } from '@/hooks/useOrgSettings';

interface ChecklistItem {
  key: string;
  label: string;
  hint: string;
  done: boolean;
}

const SetupChecklist: React.FC = () => {
  const { data: org } = useOrgProfile();

  const items = useMemo<ChecklistItem[]>(() => {
    // useOrgProfile restituisce l'org "appiattita" — settings JSONB già splattati top-level.
    return [
      {
        key: 'company_name',
        label: 'Ragione sociale',
        hint: 'Visibile nell\'header dei preventivi PDF',
        done: !!(org?.company_name ?? org?.name)?.trim(),
      },
      {
        key: 'vat_number',
        label: 'Partita IVA',
        hint: 'Obbligatoria per fatturazione',
        done: !!org?.vat_number?.trim(),
      },
      {
        key: 'logo_url',
        label: 'Logo aziendale',
        hint: 'Compare in alto a destra sui PDF',
        done: !!org?.logo_url,
      },
      {
        key: 'address',
        label: 'Sede',
        hint: 'Indirizzo + città + CAP per intestazione PDF',
        done: !!(org?.address_line?.trim() && org?.city?.trim()),
      },
      {
        key: 'markup',
        label: 'Mark-up vendita',
        hint: 'Margine % su materiali/manodopera nel pricing engine',
        done: typeof org?.markup_materials_pct === 'number' && org.markup_materials_pct > 0,
      },
      {
        key: 'iva',
        label: 'IVA default',
        hint: 'Aliquota usata sui PDF preventivo cliente',
        done: typeof org?.iva_pct === 'number' && org.iva_pct >= 0,
      },
    ];
  }, [org]);

  const doneCount = items.filter(i => i.done).length;
  const totalCount = items.length;
  const isComplete = doneCount === totalCount;
  const progressPct = (doneCount / totalCount) * 100;

  if (isComplete) {
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-background">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="h-5 w-5" />
            Setup completo
          </CardTitle>
          <CardDescription className="text-xs">
            Tutte le configurazioni essenziali sono impostate. I tuoi preventivi sono pronti per il cliente.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/80 to-background">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Sparkles className="h-4 w-4" />
              Completa il setup ({doneCount}/{totalCount})
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Configura questi campi per generare preventivi professionali. Ognuno è opzionale ma migliora l'esperienza cliente.
            </CardDescription>
          </div>
          <div className="text-2xl font-bold text-amber-700 tabular-nums">
            {Math.round(progressPct)}%
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 w-full bg-amber-100 rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-amber-500 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-1.5 text-sm">
          {items.map(item => (
            <li
              key={item.key}
              className={`flex items-start gap-2 ${item.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}
            >
              {item.done ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <span className="font-medium">{item.label}</span>
                {!item.done && (
                  <span className="text-xs text-muted-foreground ml-2">— {item.hint}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default SetupChecklist;
